import React from 'react';
import styled from 'styled-components';
import { ModalSubtitle, ModalTitle, theme } from '@mesa-labs/mesa-ui';

export type PayoutMode = 'ON_DEMAND' | 'INSTANT_PAYOUT';

export const ModalContent = styled.div<{ background?: string, overflow?: string, padding?: string, gap?: number }>`
  background: ${(props) => props.background || props.theme.colors.White};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.gap || 0}px;
  height: 100%;
  overflow: ${(props) => props.overflow || 'hidden'};
  padding: ${(props) => props.padding || '40px'};
  position: relative;
  text-align: left;
  width: 100%;
`;

export const ModalHeading = styled(ModalTitle)`
  color: ${theme.colors.Navy};
`;

export const ModalHeadingHighlight = styled.span`
  color: ${theme.colors.Indigo};
`;

export const ModalSupportingText = styled(ModalSubtitle)`
  color: #525866;
  font-size: 16px;
  line-height: 24px;
`;

export const TopGradientBlur = styled.div`
  border-radius: 30px;
  background: #4054ED;
  filter: blur(92px);
  height: 114px;
  position: absolute;
  right: -50px;
  top: -50px;
  width: 114px;
`;

export const ModalBody = styled.div<{ gap?: number, marginBottom?: number }>`
  color: ${(props) => props.theme.colors.Gray600};
  display: flex;
  flex-direction: column;
  font-size: 14px;
  gap: ${(props) => props.gap || 20}px;
  line-height: 20px;
  margin-bottom: ${(props) => props.marginBottom ?? 20}px;
  text-align: center;
`;

export const ModalActions = styled.div<{ gridTemplateColumns?: string, gap?: number }>`
  align-items: center;
  display: grid;
  gap: ${(props) => props.gap ?? 12}px;
  grid-template-columns: ${(props) => props.gridTemplateColumns || '1fr 3fr'};
`;

const CloseButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  border-radius: 8px;
  color: #99A0AE;
  cursor: pointer;
  display: flex;
  height: 44px;
  justify-content: center;
  padding: 8px;
  position: absolute;
  right: 8px;
  top: 8px;
  transition: color 0.2s ease-in-out;
  width: 44px;

  &:hover {
    color: #525866;
  }
`;

export const ModalCloseButton = ({ onClick }: { onClick: () => void }) => (
  <CloseButton type="button" aria-label="Close" onClick={onClick}>
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 6L12 12M12 12L6 18M12 12L6 6M12 12L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </CloseButton>
);

export const ModalTable = styled.div<{ marginBottom?: number }>`
  background: ${(props) => props.theme.colors.White};
  border-radius: 4px;
  filter: drop-shadow(0 4px 3px rgba(10, 13, 18, 0.03));
  overflow: hidden;
  margin-bottom: ${(props) => props.marginBottom || 0}px;
  padding: 20px 20px 8px;
`;

export const ModalTableSection = styled.div<{ header?: boolean, align?: 'center' | 'flex-start' | 'flex-end' }>`
  align-items: ${(props) => props.align || 'center'};
  background: ${(props) => props.theme.colors.White};
  border-radius: 6px 6px 0px 0px;
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  width: 100%;

  ${(props) => props.header && `
    background: #F8FAFB;
    margin-bottom: 8px;
  `}

  ${(props) => !props.header && `
    min-height: 48px;
    padding: 12px;
    position: relative;

    &:not(:last-of-type) {
      margin-bottom: 12px;

      &::after {
        background: #ECEEF3;
        bottom: -6.5px;
        content: '';
        height: 1px;
        left: 12px;
        position: absolute;
        right: 12px;
      }
    }

    &:not(:last-of-type):nth-last-of-type(2) {
      margin-bottom: 9px;

      &::after {
        bottom: -7.5px;
        height: 2px;
      }
    }
  `}

  & > div {
    flex-basis: 50%;
  }
`;

export const ModalTableText = styled.div<{ header?: boolean, bold?: boolean, total?: boolean, weight?: number, align?: 'center' | 'left' | 'right' }>`
  color: #40396C;
  font-size: 14px;
  line-height: 20px;
  text-align: ${(props) => props.align || 'left'};

  ${(props) => props.header && `
    color: #6D6790;
  `}

  ${(props) => props.bold && `
    color: ${props.theme.colors.Gray900};
    font-weight: 600;
  `}

  ${(props) => props.total && `
    color: ${props.theme.colors.Navy};
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
  `}

  ${(props) => props.weight && `
    font-weight: ${props.weight};
  `}
`;

export const ModalTableTextBlue = styled(ModalTableText)`
  background: linear-gradient(180deg, #4700D1 0%, #5A05FF 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;


export const NotificationContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 40px 20px;
`;

export const NotificationIcon = styled.img`
  align-self: center;
  margin-bottom: 24px;
  width: 64px;
`;

export const NotificationTitle = styled.div`
  font-family: ${(props) => props.theme.fonts.inter};
  font-size: 28px;
  font-weight: 600;
  line-height: 136%;
  margin-bottom: 17px;
  text-align: center;
`;

export const NotificationText = styled.div<{ maxWidth?: string | number }>`
  color: #9F9F9F;
  font-size: 14px;
  line-height: 150%;
  font-size: 14px;
  line-height: 154%;
  max-width: ${(props) => (props.maxWidth === 'string') ? props.maxWidth : (props.maxWidth || 430)}px;
  text-align: center;
`;

export const UploadAddendum = styled.div`
  background: #F6FAFF;
  border-radius: 8px;
  color: ${(props) => props.theme.colors.Gray700};
  font-size: 12px;
  padding: 12px;
  text-align: left;  
`;
